import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { IpsRequestsService } from '@app/ips/services/ips-requests.service';
import { ScreeningService } from '@app/store/screening/screening.service';
import { ScreeningState } from '@app/store/screening/screening.state';
import { Store } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';
import { catchError, take, tap } from 'rxjs';
import { ModalDivergenceReportPreviewComponent } from '../modal-divergence-report-preview/modal-divergence-report-preview.component';
import { IImageDescription, IScreeningDivergenceReportResponse } from '../../models';
import { ScreeningStateModel } from '@app/store/screening';
import { AddTask } from '@app/store/tasks/tasks.actions';
import { FileUploadService } from '@app/shared/services/file-upload/file-upload.service';

@Component({
  selector: 'app-divergence-report',
  templateUrl: './divergence-report.component.html',
  styleUrls: ['./divergence-report.component.sass', '../../screening.sass'],
  providers: [IpsRequestsService, ScreeningService],
})
export class DivergenceReportComponent implements OnInit {
  @Input() private nextStep: any;
  @Output() completedChange = new EventEmitter<boolean>();
  @Output() hasDivergenceReportChange = new EventEmitter<boolean>();

  tradeInfo!: IScreeningDivergenceReportResponse;
  idTrade: string;
  defaultEvidences: IImageDescription[] = [];
  previewDisabled = true;
  isImageSaved = false;
  imagesFromGallery = false;
  inputs: any;

  constructor(
    public _store: Store,
    private _router: Router,
    private _toastrService: ToastrService,
    private _requestsService: IpsRequestsService,
    private _screeningService: ScreeningService,
    private _dialog: MatDialog,
    private __uploadService: FileUploadService
  ) {}

  ngOnInit(): void {
    const { idTrade } = this._store.selectSnapshot(ScreeningState);
    this.idTrade = idTrade;
    this._getDivergenceReport();
  }

  isInvalid() {
    const invalidNonExtraEvidence = this.tradeInfo.evidences
      .filter((evidence) => !evidence.isExtra)
      .some((evidence) => evidence.src === '');

    const evidencesHaveInvalidDescription = this.tradeInfo.evidences.some(
      (evidence) => evidence.description === ''
    );

    this.previewDisabled = invalidNonExtraEvidence || evidencesHaveInvalidDescription;
  }

  resetScreening() {
    this._screeningService.resetScreening(this.idTrade).subscribe(() => {
      this._router.navigate(['ips', 'triagem', 'buscar']);
    });
  }

  onChangeFiles(event: any) {
    this.tradeInfo.evidences = event;
    this.isInvalid();
  }

  validationSrcFiles(): boolean {
    return this.tradeInfo.evidences.some((evidence) => evidence.src === '');
  }

  openReportModalPreview() {
    const dialogRef = this._dialog.open(ModalDivergenceReportPreviewComponent, {
      panelClass: 'custom-mat-dialog',
      width: '60vw',
      height: '80vh',
      data: {
        ...this.tradeInfo,
        imagesFromGallery: this.imagesFromGallery,
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result.action === 'closeScreeing') {
        this._uploadDivergenceReportFiles(result.divergenceReportFile, result.filename);
        this._requestsService.closeScreening(result.idTrade).subscribe({
          complete: () => {
            this._toastrService.success('Triagem finalizada com sucesso.');
            this._router.navigate(['ips/triagem/buscar']);
          },
        });
      }
    });
  }

  private _uploadDivergenceReportFiles(divergenceReportFile: Blob, filename: string) {
    const payload: ScreeningStateModel = {
      idTrade: this.tradeInfo.trade.tradeId,
      blobRec: divergenceReportFile,
      videoName: filename,
      fileType: 'application/pdf',
    };
    const fullFilename = `${this.idTrade}/${filename}`;

    this._store.dispatch(
      new AddTask({
        name: payload.videoName,
        file: payload.blobRec,
        uploadPayload: payload,
        getPresignedApiUrlService: 'COLLECT.postGeneratePresignedUrl',
        preSignedRequest: {
          contentType: payload.fileType,
          filename: fullFilename,
          idTrade: this.idTrade,
        },
        updateGalleryApiUrlService: 'COLLECT.postRegisterUploadedMedia',
        updateTradeGalleryRequest: {
          idTrade: this.idTrade,
          filename: fullFilename,
        },
        successMessage: `Arquivo ${payload.videoName} finalizado com sucesso.`,
        errorMessage: `Não foi possível enviar a evidência ${payload.videoName}. Por favor, tente novamente.`,
      })
    );
  }

  private async _getDivergenceReport() {
    try {
      const { images } = await this.__uploadService
        .getGalleryByTradeId(this.idTrade, 'COLLECT.getTradeGallery')
        .pipe(take(1))
        .toPromise();

      const data = await this._screeningService
        .getDivergenceReport(this.idTrade)
        .pipe(take(1))
        .toPromise();

      this.hasDivergenceReportChange.emit(true);
      this.tradeInfo = data;
      const { photos } = this.tradeInfo.divergenceReason;

      this.defaultEvidences = photos.map((subtitle) => {
        let matchedImageUrl = '';

        images.forEach((imgUrl) => {
          const fileName = this.extractFileName(imgUrl);

          if (fileName.includes(subtitle)) {
            matchedImageUrl = imgUrl;
          }
        });

        return {
          src: matchedImageUrl || '',
          description: subtitle,
          isExtra: false,
        };
      });

      this.tradeInfo.evidences = this.defaultEvidences;
      this.imagesFromGallery = images.length > 0;
    } catch (error) {
      console.error('Erro ao obter o relatório de divergência:', error);
    }
  }

  private extractFileName(url: string): string {
    const match = url.match(/\/([^/]+)$/);
    return match ? match[1] : '';
  }
}
